import dayjs from "dayjs";
import type { useTranslations } from "use-intl";
import {
  coerce,
  date,
  discriminatedUnion,
  nativeEnum,
  number,
  object,
} from "zod";
import type { ZodType } from "zod";

import { airportSchema, citySchema } from "@/app/lib/utils/locations";
import { CabinClass, TripType } from "@/types/enums";
import type { TFlightFormFieldValues } from "@/types/types";

export const flightFormLocationSchema = discriminatedUnion("type", [
  airportSchema,
  citySchema,
]);

export const flightFormValuesSchema: ZodType<TFlightFormFieldValues> = object({
  tripType: nativeEnum(TripType),
  from: flightFormLocationSchema.nullable(),
  to: flightFormLocationSchema.nullable(),
  dates: object({
    departure: coerce.date(),
    return: coerce.date().optional(),
  }),
  adult: number().min(1).max(9),
  child: number().min(0).max(9),
  infantWithoutSeat: number().min(0).max(9),
  cabinClass: nativeEnum(CabinClass),
});

export const getValidationSchema = (t: ReturnType<typeof useTranslations>) =>
  object({
    dates: object({
      departure: date({
        required_error: t("flights.validation.dates.departure"),
      }).refine((date) => {
        if (!date) return true;
        return !dayjs(date).endOf("day").isBefore(dayjs(), "day");
      }, t("flights.validation.dates.minDeparture")),
      return: date().optional(),
    }),
    from: flightFormLocationSchema
      .nullable()
      .refine((value) => value !== null, t("flights.validation.from")),
    to: flightFormLocationSchema
      .nullable()
      .refine((value) => value !== null, t("flights.validation.to")),
    tripType: nativeEnum(TripType, {
      required_error: t("flights.validation.tripType"),
    }),
    cabinClass: nativeEnum(CabinClass, {
      required_error: t("flights.validation.cabinClass"),
    }),
    adult: number().min(1).max(9),
    child: number().min(0).max(9),
    infantWithoutSeat: number().min(0).max(9),
  }).superRefine((values, ctx) => {
    const { dates, tripType } = values;
    const { departure: departureDate, return: returnDate } = dates;

    if (tripType === TripType.ROUND_TRIP && !returnDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dates", "return"],
        message: t("flights.validation.dates.returnRequired"),
      });
    }

    if (returnDate && departureDate && returnDate < departureDate) {
      ctx.addIssue({
        code: "custom",
        path: ["dates", "return"],
        message: t("flights.validation.dates.minReturn"),
      });
    }
  });

export type FlightFormValuesSchema = ReturnType<typeof getValidationSchema>;
